import { useState, useEffect, useCallback, useRef } from 'react';
import { ticketsAPI, actasAPI, pazYSalvoAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useSubroles } from '../contexts/SubrolesContext';

export function usePendingCounts() {
    const { user } = useAuth();
    const userId = user?.id ?? null;
    const userRole = user?.role ?? null;

    const { getActiveSubroles } = useSubroles();
    const [counts, setCounts] = useState({ tickets: 0, actas: 0, pazysalvo: 0 });

    // Ref estable para getActiveSubroles — evita que su referencia cause re-creación de fetchCounts
    const getActiveSubrolesRef = useRef(getActiveSubroles);
    getActiveSubrolesRef.current = getActiveSubroles;

    // Ref para el user completo (necesitamos user.id en el callback, pero sin ponerlo en deps)
    const userRef = useRef(user);
    userRef.current = user;

    const fetchCounts = useCallback(async () => {
        if (!userId) return;

        try {
            let ticketsCount = 0;
            let actasCount = 0;
            let pazysalvoCount = 0;

            // 1. Tickets pendientes
            try {
                const res = await ticketsAPI.getMisTickets();
                const tickets = Array.isArray(res.data) ? res.data : [];
                ticketsCount = tickets.filter(t => {
                    const est = t.estado?.toLowerCase() || '';
                    return est === 'esperando usuario' || est === 'falta de calificacion' || (est === 'resuelto' && !t.feedback);
                }).length;
            } catch (err) { console.error('Error fetching tickets for pending counts', err); }

            // 2. Actas pendientes
            try {
                const res = await actasAPI.getMisActas();
                const actas = Array.isArray(res.data) ? res.data : [];
                actasCount = actas.filter(a => {
                    const est = a.estado?.toLowerCase() || '';
                    return est === 'pendiente' || est === 'rechazada';
                }).length;
            } catch (err) { console.error('Error fetching actas for pending counts', err); }

            // 3. Paz y Salvo pendientes de firma
            try {
                const res = await pazYSalvoAPI.getAll();
                const pazYSalvos = Array.isArray(res.data?.data) ? res.data.data : (Array.isArray(res.data) ? res.data : []);
                const currentUser = userRef.current;
                const userRoles = ['JefeInmediato'];
                const activeSubroles = getActiveSubrolesRef.current?.() || [];
                activeSubroles.forEach(sr => userRoles.push(sr.rol));
                if (userRole === 'admin') userRoles.push('Admin');

                pazYSalvos.forEach(ps => {
                    if (ps.estado === 'Cerrado' || ps.estado === 'Rechazado') return;
                    const firmas = Array.isArray(ps.firmas) ? ps.firmas : [];
                    const needsMySignature = firmas.some(f => {
                        if (f.estado !== 'Pendiente') return false;
                        if (f.firmanteUserId === currentUser?.id) return true;
                        if (!f.firmanteUserId && userRoles.includes(f.rolConfigurado)) return true;
                        return false;
                    });
                    if (needsMySignature) pazysalvoCount++;
                });
            } catch (err) { console.error('Error fetching paz y salvo for pending counts', err); }

            setCounts({ tickets: ticketsCount, actas: actasCount, pazysalvo: pazysalvoCount });
        } catch (error) {
            console.error('Error fetching pending counts:', error);
        }
    }, [userId, userRole]); // Solo primitivos estables — no el objeto user completo

    useEffect(() => {
        fetchCounts();
        const interval = setInterval(fetchCounts, 120000); // poll cada 2 min
        return () => clearInterval(interval);
    }, [fetchCounts]);

    return counts;
}
